import type { Rect, Stroke } from './stroke';
import { rectsIntersect } from './stroke';
import { buildRibbon, fillRibbon } from './ribbon';
import { drawTextBox, textBoxRect, textBoxFont } from './text';
import { resolveInk, isHighlight, paperColor, type Theme } from '../theme/tokens';
import { drawTemplate } from '../theme/templates';
import { pageRectFor } from './renderer';
import type { Page, TextBox } from '../store/types';

const INFINITE_PAD = 40; // world px padding around content bbox on infinite pages
const EMPTY_INFINITE_RECT: Rect = { x: 0, y: 0, w: 400, h: 300 }; // 4:3 fallback

function unionRects(rects: Rect[]): Rect {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const r of rects) {
    if (r.x < minX) minX = r.x;
    if (r.y < minY) minY = r.y;
    if (r.x + r.w > maxX) maxX = r.x + r.w;
    if (r.y + r.h > maxY) maxY = r.y + r.h;
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

function worldRectFor(page: Page, strokes: Stroke[]): Rect {
  const fixed = pageRectFor(page);
  if (fixed) return fixed;
  const active = strokes.filter((s) => !s.deletedAt);
  if (active.length === 0) return { ...EMPTY_INFINITE_RECT };
  const union = unionRects(active.map((s) => s.bbox));
  return {
    x: union.x - INFINITE_PAD,
    y: union.y - INFINITE_PAD,
    w: union.w + INFINITE_PAD * 2,
    h: union.h + INFINITE_PAD * 2,
  };
}

/** Layout math shared by renderThumbnail and any future thumbnail consumer. */
export function thumbLayout(
  page: Page,
  strokes: Stroke[],
  maxW: number,
  maxH: number,
): { world: Rect; w: number; h: number; scale: number } {
  const world = worldRectFor(page, strokes);
  const rawScale = Math.min(maxW / world.w, maxH / world.h);
  const w = Math.max(1, Math.round(world.w * rawScale));
  const h = Math.max(1, Math.round(world.h * rawScale));
  const scale = w / world.w;
  return { world, w, h, scale };
}

/**
 * Renders a page thumbnail: paper fill, template, then committed content at
 * thumbnail scale — highlighter, then text, then pen (same pass ordering as
 * the tile renderer; ink annotates over text, highlighter stays under
 * everything). Device-pixel scale is always 1x — thumbnails don't need
 * retina sharpness.
 *
 * Note (v1 scope, per plan): the infinite-page content bbox (worldRectFor)
 * unions stroke bboxes only, not text-box rects — a text-only infinite page
 * still lays out around the empty-page fallback rect. Fixed pages are
 * unaffected (their world rect is always the full A4 page).
 */
export function renderThumbnail(
  page: Page,
  strokes: Stroke[],
  textBoxes: TextBox[],
  theme: Theme,
  maxW: number,
  maxH: number,
): HTMLCanvasElement {
  const { world, w, h, scale } = thumbLayout(page, strokes, maxW, maxH);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  ctx.fillStyle = paperColor(theme);
  ctx.fillRect(0, 0, w, h);

  ctx.save();
  ctx.scale(scale, scale);
  ctx.translate(-world.x, -world.y);
  const pageRect = pageRectFor(page);
  if (pageRect) {
    ctx.beginPath();
    ctx.rect(pageRect.x, pageRect.y, pageRect.w, pageRect.h);
    ctx.clip();
  }
  drawTemplate(ctx, page.templateId, theme, world, scale, pageRect ?? world);
  drawStrokePass(ctx, strokes, world, theme, 'highlighter');
  for (const box of textBoxes) {
    if (box.deletedAt) continue;
    ctx.save();
    ctx.font = textBoxFont(box);
    const rect = textBoxRect(box, (s) => ctx.measureText(s).width);
    ctx.restore();
    if (!rectsIntersect(rect, world)) continue;
    drawTextBox(ctx, box, theme);
  }
  drawStrokePass(ctx, strokes, world, theme, 'pen');
  ctx.restore();
  return canvas;
}

function drawStrokePass(ctx: CanvasRenderingContext2D, strokes: Stroke[], world: Rect, theme: Theme, pass: 'highlighter' | 'pen') {
  for (const s of strokes) {
    if (s.tool !== pass || s.deletedAt) continue;
    if (!rectsIntersect(s.bbox, world)) continue;
    ctx.save();
    if (isHighlight(s.color) || s.tool === 'highlighter') ctx.globalCompositeOperation = 'multiply';
    ctx.fillStyle = resolveInk(s.color, theme);
    fillRibbon(ctx, buildRibbon(s.points, s.baseWidth));
    ctx.restore();
  }
}

/** Canonical iOS canvas memory release: zero the backing store. */
function releaseCanvas(c: HTMLCanvasElement): void {
  c.width = 0;
  c.height = 0;
}

/** LRU cache of rendered thumbnail canvases, keyed `pageId@rev@theme` so edits self-invalidate. */
export class ThumbCache {
  private map = new Map<string, HTMLCanvasElement>();
  constructor(private cap = 60) {}

  private key(pageId: string, rev: number, theme: Theme): string {
    return `${pageId}@${rev}@${theme}`;
  }

  getOrRender(page: Page, strokes: Stroke[], textBoxes: TextBox[], theme: Theme, maxW: number, maxH: number): HTMLCanvasElement {
    const key = this.key(page.id, page.rev, theme);
    const hit = this.map.get(key);
    if (hit) {
      this.map.delete(key);
      this.map.set(key, hit); // bump recency
      return hit;
    }
    const canvas = renderThumbnail(page, strokes, textBoxes, theme, maxW, maxH);
    this.map.set(key, canvas);
    while (this.map.size > this.cap) {
      const oldestKey = this.map.keys().next().value as string;
      const oldest = this.map.get(oldestKey)!;
      this.map.delete(oldestKey);
      releaseCanvas(oldest);
    }
    return canvas;
  }

  clear() {
    for (const c of this.map.values()) releaseCanvas(c);
    this.map.clear();
  }
}

export const thumbCache = new ThumbCache();
